import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { Search, Loader2, CornerDownLeft, Palette } from 'lucide-react';
import { usePermissions } from '../../context/PermissionContext';
import { ADMIN_NAV } from './adminNav';
import { ColorImage } from './fabric/ColorImage';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Quick search at the top of the admin sidebar: jumps to any page or any fabric color.
export const SidebarQuickSearch = ({ onSelectTab, onSelectColor, collapsed, onExpand }) => {
  const { canView, hasPermission } = usePermissions();
  const [q, setQ] = useState('');
  const [colors, setColors] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [cursor, setCursor] = useState(0);
  const inputRef = useRef(null);
  const boxRef = useRef(null);

  const pages = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [];
    const hit = (label) => label.toLowerCase().split(/[\s&/]+/).some((w) => w.startsWith(needle)) || (needle.length >= 4 && label.toLowerCase().includes(needle));
    return ADMIN_NAV.flatMap((cat) => {
      if (cat.module && hasPermission && !hasPermission(cat.module, 'view')) return [];
      return cat.items
        .filter((i) => !i.can || canView(i.can))
        .filter((i) => hit(i.label) || hit(cat.label))
        .map((i) => ({ ...i, category: cat.label }));
    }).slice(0, 6);
  }, [q, canView, hasPermission]);

  useEffect(() => {
    const needle = q.trim();
    if (needle.length < 2) { setColors([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const { data } = await axios.get(`${API_URL}/api/fabric-collections/search-colors?q=${encodeURIComponent(needle)}&limit=8`);
        setColors(data?.results || []);
      } catch { setColors([]); } finally { setSearching(false); }
    }, 220);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onExpand?.();
        setTimeout(() => inputRef.current?.focus(), 50);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onExpand]);

  useEffect(() => {
    const onDoc = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const results = useMemo(() => [
    ...pages.map((p) => ({ type: 'page', key: `p-${p.value}`, item: p })),
    ...colors.map((c) => ({ type: 'color', key: `c-${c.id}`, item: c })),
  ], [pages, colors]);
  useEffect(() => setCursor(0), [results.length]);

  const pick = (r) => {
    setOpen(false); setQ('');
    if (r.type === 'page') onSelectTab(r.item.value);
    else onSelectColor(r.item);
  };

  const onKeyDown = (e) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => Math.min(c + 1, results.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => Math.max(c - 1, 0)); }
    else if (e.key === 'Enter' && results[cursor]) { e.preventDefault(); pick(results[cursor]); }
    else if (e.key === 'Escape') { setOpen(false); inputRef.current?.blur(); }
  };

  if (collapsed) {
    return (
      <button type="button" onClick={() => { onExpand?.(); setTimeout(() => inputRef.current?.focus(), 60); }} className="mx-auto mt-2 mb-1 p-2 rounded-lg text-neutral-500 hover:bg-neutral-100 hover:text-neutral-900 flex" title="Search (Ctrl+K)" data-testid="sidebar-search-collapsed-btn">
        <Search className="w-4 h-4" />
      </button>
    );
  }

  return (
    <div className="relative px-2 pt-2 pb-1" ref={boxRef} data-testid="sidebar-quick-search">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-neutral-400" />
        <input
          ref={inputRef}
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
          placeholder="Jump to page or color…"
          className="w-full h-9 pl-8 pr-12 rounded-lg bg-neutral-100 border border-transparent focus:bg-white focus:border-amber-400 focus:ring-2 focus:ring-amber-100 outline-none text-[13px] placeholder:text-neutral-400"
          data-testid="sidebar-search-input"
          aria-label="Quick search"
        />
        <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-neutral-400 border border-neutral-200 rounded px-1 py-px bg-white">⌘K</kbd>
      </div>

      {open && q.trim() && (
        <div className="absolute left-2 top-full mt-1 z-50 w-[22rem] bg-white border border-neutral-200 rounded-lg shadow-xl overflow-hidden" data-testid="sidebar-search-results">
          {results.length === 0 && !searching ? (
            <p className="px-3 py-3 text-xs text-neutral-500" data-testid="sidebar-search-empty">No page or color matches “{q.trim()}”</p>
          ) : (
            <ul className="max-h-80 overflow-y-auto py-1">
              {pages.length > 0 && <li className="px-3 pt-1.5 pb-0.5 text-[10px] uppercase tracking-wider text-neutral-400">Pages</li>}
              {pages.map((p, i) => {
                const Icon = p.icon; const active = cursor === i;
                return (
                  <li key={p.value}>
                    <button type="button" onMouseEnter={() => setCursor(i)} onClick={() => pick({ type: 'page', item: p })} className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left text-[13px] ${active ? 'bg-amber-50 text-amber-900' : 'text-neutral-700 hover:bg-neutral-50'}`} data-testid={`sidebar-search-page-${p.value}`}>
                      <Icon className="w-3.5 h-3.5 text-neutral-400 shrink-0" />
                      <span className="truncate">{p.label}</span>
                      <span className="ml-auto text-[10px] text-neutral-400 shrink-0">{p.category}</span>
                      {active && <CornerDownLeft className="w-3 h-3 text-amber-500 shrink-0" />}
                    </button>
                  </li>
                );
              })}
              {(colors.length > 0 || searching) && (
                <li className="px-3 pt-2 pb-0.5 text-[10px] uppercase tracking-wider text-neutral-400 flex items-center gap-1.5">
                  <Palette className="w-3 h-3" /> Colors {searching && <Loader2 className="w-3 h-3 animate-spin" />}
                </li>
              )}
              {colors.map((c, j) => {
                const i = pages.length + j; const active = cursor === i;
                return (
                  <li key={c.id}>
                    <button type="button" onMouseEnter={() => setCursor(i)} onClick={() => pick({ type: 'color', item: c })} className={`w-full flex items-center gap-2.5 px-3 py-1.5 text-left ${active ? 'bg-amber-50' : 'hover:bg-neutral-50'}`} data-testid={`sidebar-search-color-${c.id}`}>
                      <span className="w-6 h-6 rounded border border-neutral-200 overflow-hidden shrink-0">
                        {c.image_url ? <ColorImage src={c.image_url} alt="" w={128} eager className="w-full h-full" /> : <span className="block w-full h-full" style={{ backgroundColor: c.hex_color || '#e5e5e5' }} />}
                      </span>
                      <span className="min-w-0">
                        <span className="block text-[13px] text-neutral-900 truncate">{c.name}{c.code ? <span className="text-neutral-400"> · {c.code}</span> : null}</span>
                        <span className="block text-[10px] text-neutral-500 truncate">{[c.brand_name, c.collection_name, c.material_name].filter(Boolean).join(' › ')}</span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default SidebarQuickSearch;
